// src/contexts/OrderContext.js

import React, { createContext, useState, useEffect, useRef } from "react";
import { notification } from 'antd';
import { useUser } from "./UserContext";
import { BASE_URL, parseWarningsAndErrors, parseDate, getCookie } from "./conf/config";


export const OrderContext = createContext();

// This context is used for ordertable persistence, including searching status, table status, etc.
export const OrderProvider = ({ children }) => {

    const { orders, setOrders, refreshOrders, addNewLog, fetchWithAuth, userGroups } = useUser();

    const [pinOrders, setPinOrders] = useState([]);
    // if searchedResult exist, the table only shows searchedResult
    const [searchedResult, setSearchedResult] = useState([]);
    // cols is the columns of the table
    const [cols, setCols] = useState([]);
    // options for Select of search fields
    const [options, setOptions] = useState([]);
    // options for Select of table fields
    const [tableSelectOptions, setTableSelectOptions] = useState([]);
    // selected keys for fields of table
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    // selected keys for fields of search
    const [searchFields, setSearchFields] = useState([]);
    // selected keys for fields of table
    const [colFields, setColFields] = useState([]);
    // current page index
    const [current, setCurrent] = useState(1);
    // page size
    const [pageSize, setPageSize] = useState(20);
    // dropDownSearchItem is the options for dropdown search
    const [dropDownSearchItem, setDropDownSearchItem] = useState([]);
    // isInitialLoading is used to control the loading state of the table
    const [isInitialLoading, setIsInitialLoading] = useState(true);

    // mapping from backend field names to frontend field names, make human readable
    const fieldMapping = {
        id: "ID",
        status: "Status",
        customer: "Customer",
        order_type: "PO/SA",
        order_ID: "Order ID",
        position_ID: "Pos.",
        CPN: "CPN",
        shipping_date_planned: "ETD planned",
        order_date: "Order date",
        invoice_ID: "Invoice",
        shipping_ID: "Shipping ID",
        tracking_ID: "Tracking",
        shipping_CO: "Shipping Co.",
        shipping_method: "Ship. Meth.",
        HS: "HS",
        article_ID: "Article ID",
        quantity: "Quantity",
        unit_price: "Unit price",
        total_net: "Total net",
        incoterm: "Incoterm",
        fob_point: "FOB-point",
        inno_ID: "Innoc. ID",
        ETA: "ETA",
        shipping_date_real: "ETD eff.",
        partial_delivered_quantity: "Partial quant.",
        partially_paid: "Partially paid",
        invoice_date: "Inv. date",
        invoice_due: "Inv. due date",
        customer_date_paid: "Paid",
        stwxe_invoice_ID: "STWXE inv.",
        inno_paid_date: "STWXE paid",
        inv_sig: "Inventory sign.",
        change_date: "Change date",
        cancellation_ID: "Cancel ID",
        cancellation_date: "Cancel date",
        ctn: "CTNs",
        gross_weight: "Weight gr./kg",
        net_weight: "Weight net/kg",
        note: "Note",
        credit_amount: "Credit amount",
        credit_ID: "Credit ID",
        credit_date: "Credit date",
        batches: "Batches",
        batch_quantities: "Batch qty.",
        stwxe_invoice_date: "STWXE inv. date",
        palettes: "Palettes",
        stwxe_unit_price: "STWXE unit price",
        Gross_weight_ctn: "Gross wt./CTN",
        weight_palette: "Wt./palette",
        taxable: "Taxable",
        dimension_ctn: "CTN dimensions",
        qty_per_ctn: "Qty/CTN",
        price_agreement: "Price agr.",
        replaced_invoice_ID: "Replaced inv. ID",
        contact_user: "Contact user",
    };

    const fieldTooltips = {
        id: "Internal database record ID (auto-assigned)",
        status: "Order lifecycle stage: fresh → stwxe → shipped → paid",
        customer: "Customer name",
        order_type: "PO = Purchase Order (customer sends PO); SA = Sales Agreement",
        order_ID: "Customer's purchase order number",
        position_ID: "Line item position within the PO; sub-positions get a .1 suffix",
        CPN: "Customer part number",
        shipping_date_planned: "Planned export departure date (ETD planned)",
        order_date: "Date the order was placed",
        invoice_ID: "Innocoso invoice number",
        shipping_ID: "Freight forwarder shipment reference number",
        tracking_ID: "Carrier parcel tracking number",
        shipping_CO: "Freight forwarder / shipping company name",
        shipping_method: "Transport mode (e.g. air, sea, DHL)",
        HS: "HS tariff classification code for customs",
        article_ID: "Innocoso internal article / product number",
        quantity: "Quantity shipped or to be shipped",
        unit_price: "Price per unit",
        total_net: "Total net value (unit price × quantity)",
        incoterm: "Delivery term (e.g. DAP, FOB, CIF)",
        fob_point: "FOB / delivery point location",
        inno_ID: "Innocoso internal order reference, assigned at the Stwxe step",
        ETA: "Estimated arrival date at the destination",
        shipping_date_real: "Actual export departure date (effective ETD)",
        partial_delivered_quantity: "Quantity delivered so far, used for consignment orders",
        partially_paid: "Total quantity paid so far across all consignment sub-orders",
        invoice_date: "Date the invoice was issued",
        invoice_due: "Invoice payment due date",
        customer_date_paid: "Date customer payment was received",
        stwxe_invoice_ID: "Stwxe export document number",
        inno_paid_date: "Date Innocoso paid the Stwxe invoice",
        inv_sig: "Inventory signature — +/- country codes for stock booking (e.g. -DE+CN)",
        change_date: "Date of the last edit to this order",
        cancellation_ID: "Invoice cancellation document number",
        cancellation_date: "Date the invoice was cancelled",
        ctn: "Number of cartons in the shipment",
        gross_weight: "Total gross weight of the shipment in kg",
        net_weight: "Total net weight of the shipment in kg",
        note: "Free-text notes on this order",
        credit_amount: "Credit note amount (negative value, same currency as invoice)",
        credit_ID: "Credit note document number",
        credit_date: "Date the credit note was issued",
        batches: "Batch numbers of the shipped goods",
        batch_quantities: "Quantities per batch, comma-separated",
        stwxe_invoice_date: "Date of the Stwxe export invoice",
        palettes: "Number of palettes in the shipment",
        stwxe_unit_price: "Unit price on the Stwxe invoice (may differ from customer price)",
        Gross_weight_ctn: "Gross weight per carton in kg",
        weight_palette: "Total weight per palette in kg",
        taxable: "Taxability flag: 'Taxable' or 'Not taxable in Germany'",
        dimension_ctn: "Carton dimensions (L × W × H in cm)",
        qty_per_ctn: "Pieces per carton",
        price_agreement: "Special pricing notes or agreed price reference",
        replaced_invoice_ID: "Invoice ID that this order replaces after cancellation and re-invoice",
        contact_user: "Django username of the contact person assigned to this order",
    };

    // hard code status options
    const statusOptions = [
        { label: 'fresh', value: 'fresh' },
        { label: 'invoice cancelled', value: 'invoice_cancelled' },
        { label: 'shipped', value: 'shipped' },
        { label: 'stwxe', value: 'stwxe' },
        { label: 'cancelled', value: 'cancelled' },
        { label: 'paid', value: 'paid' },
        { label: 'consigned', value: 'consigned' },
        { label: 'all', value: '' },
        { label: 'credit', value: 'credit'},
        { label: 'credit used', value: 'credit_used'},
    ];

    const defaultSearchItem = ["id", "status", "customer", "order_type", "order_ID"];

    const defaultColumnFields = userGroups.includes('admin') ? ["id", "status", "customer", "order_ID", "position_ID", "CPN", "invoice_ID", "shipping_ID", "tracking_ID", "shipping_date_real",  "quantity","shipping_co"] : ['id', 'status', 'customer', 'order_ID', 'position_ID', 'CPN', 'quantity', 'shipping_ID', 'tracking_ID', 'shipping_date_planned', 'shipping_date_real'];

    const GenerateFileRef = useRef();

    // update cols if colFields changed
    const setupData = () => {
        if (orders.length > 0) {
            const firstRecord = orders[0];
            if (typeof firstRecord !== 'object') {
                throw new TypeError('Unsupported response type');
            } else {
                setSearchFields(defaultSearchItem.map(item => ({
                    label: fieldMapping[item] || item,
                    value: item
                })));
                setColFields(defaultColumnFields.map(item => ({
                    label: fieldMapping[item] || item,
                    value: item
                })));
                const options = Object.keys(firstRecord).map(key => ({
                    value: key,
                    label: fieldMapping[key] || key
                }));
                const searchItems = Object.keys(firstRecord).map((key, index) => ({
                    label: fieldMapping[key] || key,
                    key: index,
                    value: key
                }));
                setOptions(options);
                setTableSelectOptions(firstRecord);
                setDropDownSearchItem(searchItems);
            }
            setIsInitialLoading(false);
        }
    };

    // update searchedResult if orders changed
    useEffect(() => {
        if (searchedResult.length > 0) {
            const updatedSearchedResult = searchedResult.map(searchItem => {
                const matchingOrder = orders.find(order => order.id === searchItem.id);
                if (matchingOrder) {
                    // replace the matching order with the updated order
                    return { ...matchingOrder };
                }
                return searchItem;
            });
            setSearchedResult(updatedSearchedResult);
        }
    }, [orders]);

    // only run once when isInitialLoading is true
    useEffect(() => {
        if (isInitialLoading) {
            setupData();
        }
    }, [orders]);

    // reorder the data if it is pinned
    const handlePinClick = (record) => {
        let newPinOrders = [];
        const exist = pinOrders.some(order => order.id === record.id);
        if (exist) {
            newPinOrders = pinOrders.filter(order => order.id !== record.id);
        } else {
            record["isTop"] = true;
            newPinOrders = [...pinOrders, record];
        }
        setPinOrders(newPinOrders);
    };

    // enable table sortor function
    const getSorter = (field) => (a, b, sortOrder) => {
        const isDateField = field.toLowerCase().includes("date");

        if (sortOrder === 'ascend') {
            if (a.isTop !== b.isTop) {
                return a.isTop ? -1 : 1;
            }
        } else if (sortOrder === 'descend') {
            if (a.isTop !== b.isTop) {
                return a.isTop ? 1 : -1;
            }
        }

        // order date data
        if (isDateField) {
            const dateA = parseDate(a[field]);
            const dateB = parseDate(b[field]);
            return dateA - dateB;
        }

        // if the string can convert to number, sort by number
        const numA = parseFloat(a[field]);
        const numB = parseFloat(b[field]);
        if (!isNaN(numA) && !isNaN(numB)) {
            return numA - numB;
        }

        // last sort by string
        return a[field].localeCompare(b[field]);
    };


    return (
        <OrderContext.Provider
            value={{
                orders, setOrders, refreshOrders, addNewLog, fetchWithAuth,
                pinOrders, setPinOrders,
                searchedResult, setSearchedResult,
                cols, setCols,
                options, setOptions,
                tableSelectOptions, setTableSelectOptions,
                selectedRowKeys, setSelectedRowKeys,
                searchFields, setSearchFields,
                colFields, setColFields,
                current, setCurrent,
                pageSize, setPageSize,
                dropDownSearchItem, setDropDownSearchItem,
                statusOptions,
                setupData, handlePinClick, getSorter,
                GenerateFileRef,
                fieldMapping,
                fieldTooltips,
            }}
        >
            {children}
        </OrderContext.Provider>
    );
};
